import { useEffect, useState } from "react";
import { Button, Card, Spinner, TextInput, Label } from "flowbite-react";
import { supabase } from "../../lib/supabase";
import { useNavigate } from "react-router-dom";
import { HiCheckCircle, HiXCircle, HiOutlineLockClosed } from "react-icons/hi";

const SetPasswordPage = () => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Verify the password reset token is valid
    const verifyToken = async () => {
      try {
        // Check if we have a hash parameter with token
        const hash = window.location.hash;
        if (!hash) {
          setError(
            "Invalid or missing reset token. Please request a new invitation."
          );
          setLoading(false);
          return;
        }

        // Attempt to get the session to verify the hash is valid
        const { data, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          throw sessionError;
        }

        if (!data.session) {
          // If there's no session, we need to verify we're on a valid recovery page
          const params = new URLSearchParams(window.location.search);
          const type = params.get("type");

          if (type !== "recovery") {
            setError(
              "Invalid password reset link. Please request a new invitation."
            );
            setLoading(false);
            return;
          }
        }

        // Token is valid, show the password form
        setLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Verification failed");
        setLoading(false);
      }
    };

    verifyToken();
  }, []);

  const handleSetPassword = async () => {
    // Validate passwords
    setPasswordError(null);

    if (password.length < 8) {
      setPasswordError("Password must be at least 8 characters long");
      return;
    }

    if (password !== confirmPassword) {
      setPasswordError("Passwords do not match");
      return;
    }

    setSubmitting(true);

    try {
      // Update password
      const { error: updateError } = await supabase.auth.updateUser({
        password,
      });

      if (updateError) {
        throw updateError;
      }

      // Password set successfully
      setSuccess(true);

      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate("/");
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to set password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 p-4 dark:bg-gray-900">
      <Card className="max-w-md">
        <div className="flex flex-col items-center p-6 text-center">
          <h1 className="mb-4 text-2xl font-bold">Set Your Password</h1>

          {loading && (
            <div className="flex flex-col items-center space-y-4">
              <Spinner size="xl" />
              <p className="text-gray-500 dark:text-gray-400">
                Verifying your invitation...
              </p>
            </div>
          )}

          {error && (
            <div className="mt-4 text-center">
              <div className="mb-4 flex justify-center">
                <HiXCircle className="h-16 w-16 text-red-500" />
              </div>
              <h2 className="mb-2 text-xl font-semibold text-red-800">
                Verification Failed
              </h2>
              <p className="mb-4 text-gray-700 dark:text-gray-400">{error}</p>
              <Button
                color="failure"
                onClick={() => navigate("/authentication/sign-in")}
              >
                Back to Login
              </Button>
            </div>
          )}

          {success && (
            <div className="mt-4 text-center">
              <div className="mb-4 flex justify-center">
                <HiCheckCircle className="h-16 w-16 text-green-500" />
              </div>
              <h2 className="mb-2 text-xl font-semibold text-green-800">
                Password Set Successfully
              </h2>
              <p className="mb-4 text-gray-700 dark:text-gray-400">
                Your password has been set successfully. You will be redirected
                to the dashboard.
              </p>
            </div>
          )}

          {!loading && !error && !success && (
            <div className="w-full">
              <div className="mb-4 flex justify-center">
                <HiOutlineLockClosed className="h-16 w-16 text-blue-500" />
              </div>

              <p className="mb-6 text-gray-700 dark:text-gray-400">
                Please set a password for your account to complete the
                registration process.
              </p>

              {passwordError && (
                <div className="mb-4 rounded-lg border border-red-300 bg-red-50 p-3 text-red-800">
                  {passwordError}
                </div>
              )}

              <div className="mb-4">
                <div className="mb-2 block">
                  <Label htmlFor="password">New Password</Label>
                </div>
                <TextInput
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="sentry-unmask"
                />
              </div>

              <div className="mb-6">
                <div className="mb-2 block">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                </div>
                <TextInput
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="sentry-unmask"
                />
              </div>

              <Button
                color="primary"
                className="w-full"
                onClick={handleSetPassword}
                disabled={submitting}
              >
                {submitting ? (
                  <div className="flex items-center justify-center">
                    <Spinner size="sm" className="mr-2" />
                    Setting Password...
                  </div>
                ) : (
                  "Set Password"
                )}
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default SetPasswordPage;
